import { Link } from 'react-router'

import { documentRoute, taskRoute } from '@/app/routes'
import type { Evaluation } from '@/shared/api/queries'
import { formatDay, formatMonth, useDeadlineText } from '@/shared/format'
import { useTranslate } from '@/shared/i18n'
import type { Language } from '@/shared/i18n'
import {
  areaIcon,
  areaLabel,
  areaOf,
  documentIcon,
  documentTypeLabel,
  urgencyOf,
} from '@/shared/model'
import type { TimelineEntry, TimelineMonth } from '@/shared/model'
import { Stack, Text } from '@/shared/ui'

import './timeline.css'

function Entry({
  entry,
  evaluations,
  language,
}: {
  entry: TimelineEntry
  evaluations: Evaluation[]
  language: Language
}) {
  const t = useTranslate()
  const deadline = useDeadlineText()

  const urgency = urgencyOf({
    deadline: entry.date,
    hard_deadline: entry.task?.hard_deadline ?? false,
  })

  const [to, icon, title, meta] = entry.task
    ? [
        taskRoute(entry.task.id),
        areaIcon(areaOf(entry.task, evaluations) ?? 'other'),
        entry.task.title,
        t(areaLabel(areaOf(entry.task, evaluations) ?? 'other')),
      ]
    : [
        documentRoute(entry.document?.id ?? ''),
        documentIcon(entry.document?.type ?? 'other'),
        t('timeline.document.expires', {
          document: t(documentTypeLabel(entry.document?.type ?? '')),
        }),
        t('documents.title'),
      ]

  return (
    <li>
      <Link className={`timeline__item tone--${urgency}`} to={to} viewTransition>
        <Text as="span" role="label" tone="muted" className="timeline__date">
          {formatDay(entry.date, language)}
        </Text>
        <span className="timeline__rail" aria-hidden="true">
          <span className="timeline__dot" />
        </span>
        <span className="timeline__body">
          <Text as="span" role="body-strong">
            {icon} {title}
          </Text>
          <Text as="span" role="caption" tone="muted">
            {meta} · {deadline(entry.date)}
          </Text>
        </span>
      </Link>
    </li>
  )
}

export function TimelineEntries({
  months,
  evaluations,
  language,
  compact = false,
}: {
  months: TimelineMonth[]
  evaluations: Evaluation[]
  language: Language
  compact?: boolean
}) {
  return (
    <Stack gap="md">
      {months.map((month) => (
        <Stack key={month.key} gap="sm">
          {compact ? null : (
            <Text as="h3" role="eyebrow" tone="muted">
              {formatMonth(month.date, language)}
            </Text>
          )}
          <ul className="timeline">
            {month.entries.map((entry) => (
              <Entry key={entry.id} entry={entry} evaluations={evaluations} language={language} />
            ))}
          </ul>
        </Stack>
      ))}
    </Stack>
  )
}
